import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import CloseCircleFilledIcon from '@ant-design/icons/CloseCircleFilled'
import EyeOutlinedIcon from '@ant-design/icons/EyeOutlined'
import FileTypeIcon from '@icons/FileTypeIcon'

import Badge from '@mui/material/Badge'
import Button from '@mui/material/Button'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import IconButton from '@components/_mui/IconButton'

import { getDropzoneData, getFileType, isImage, isPdf, prettyBytes } from '@shared/utils'

import ImageFullScreenViewer from '@components/ImageFullScreenViewer'
import { PdfModal } from '@components/PdfViewer'

FilesPreview.propTypes = {
  /** Can show preview of images */
  showPreview: PropTypes.bool,

  /** Array of files */
  files: PropTypes.array,

  /** Function to remove file */
  onRemove: PropTypes.func.isRequired,

  /** Set of previewed file IDs */
  previewedFiles: PropTypes.instanceOf(Set),

  /** Callback when file is previewed */
  onFilePreview: PropTypes.func,

  /** Function to generate unique file ID */
  getFileId: PropTypes.func,
}

/**
 * Show preview of uploaded files.
 */
export default function FilesPreview({ files = [], onRemove, previewedFiles, onFilePreview, getFileId }) {
  const hasFile = files.length > 0
  const isPreviewTrackingEnabled = Boolean(previewedFiles && onFilePreview && getFileId)

  return (
    <List disablePadding sx={{ ...(hasFile && { my: 3 }) }}>
      {files.map((file, index) => {
        const { key, name } = getDropzoneData(file, index)

        const fileSize = prettyBytes(file.size)
        const fileId = isPreviewTrackingEnabled ? getFileId(file) : null
        const isPreviewed = isPreviewTrackingEnabled ? previewedFiles.has(fileId) : true

        return (
          <ListItem
            key={key}
            secondaryAction={
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                {isImage(file?.type) && <ImagePreviewButton file={file} isPreviewed={isPreviewed} onFilePreview={onFilePreview} />}
                {isPdf(file?.type) && <PdfPreviewButton file={file} name={name} isPreviewed={isPreviewed} onFilePreview={onFilePreview} />}
                {onRemove && (
                  <IconButton color="primary" onClick={() => onRemove(file)}>
                    <CloseCircleFilledIcon style={{ fontSize: '24px' }} />
                  </IconButton>
                )}
              </Stack>
            }
            sx={{
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <ListItemAvatar>
              <FileTypeIcon type={getFileType(file?.type)} style={{ fontSize: '2rem' }} />
            </ListItemAvatar>
            <ListItemText primary={typeof file === 'string' ? file : name} secondary={fileSize} />
          </ListItem>
        )
      })}
    </List>
  )
}

function ImagePreviewButton({ file, isPreviewed, onFilePreview }) {
  const previewUrl = useMemo(() => (file instanceof File ? URL.createObjectURL(file) : null), [file])

  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl])

  if (!previewUrl) return null

  return (
    <ImageFullScreenViewer image={previewUrl}>
      {(openViewer) => (
        <PreviewBtn
          isPreviewed={isPreviewed}
          onClick={() => {
            openViewer()
            onFilePreview?.(file)
          }}
        />
      )}
    </ImageFullScreenViewer>
  )
}

function PdfPreviewButton({ file, name, isPreviewed, onFilePreview }) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <PreviewBtn
        isPreviewed={isPreviewed}
        onClick={() => {
          setIsOpen(true)
          onFilePreview?.(file)
        }}
      />
      <PdfModal initialScale={1.2} file={file} filename={name} open={isOpen} onClose={() => setIsOpen(false)} title={name} />
    </>
  )
}

const PreviewBtn = ({ isPreviewed, onClick }) => (
  <Badge badgeContent="!" color="warning" invisible={isPreviewed} sx={{ '& .MuiBadge-badge': { right: 4, top: 4 } }}>
    <Button variant={isPreviewed ? 'outlined' : 'contained'} startIcon={<EyeOutlinedIcon />} onClick={onClick}>
      Preview
    </Button>
  </Badge>
)
